/**
 * Project sub-resource queries.
 *
 * One file covers parts (project-items), parts-approval submissions, order
 * batches, part-stage dates, inspection events, and Bank Guarantees. All keyed
 * by projectId; cache invalidation on every mutation refreshes the parent
 * project (lifecycle state may shift) plus the affected child list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';
import { projectQueryKey } from './project-queries';

// ---------------------------------------------------------------------------
// Parts (project items)
// ---------------------------------------------------------------------------

export type ItemType = 'PART' | 'SUB_PART';

export interface ProjectItem {
  id: string;
  projectId: string;
  parentItemId: string | null;
  itemType: ItemType;
  srNumber: number | null;
  itemNumber?: string | null;
  name: string;
  uom?: string | null;
  gstRateBps?: number | null;
  partQuantity: number | null;
  quantityPerParent: number | null;
  clientUnitPricePaisa: number | null;
  supplierUnitPricePaisa: number | null;
  makeBrand?: string | null;
  origin?: string | null;
  remarks?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
  // Derived
  totalRequiredQuantity: number;
  activeOrderedQuantity: number;
  currentStageNumber: number;
  isReadyForPartStages: boolean;
  daysInCurrentStage: number;
  partialBatchIndicator: string | null;
  deliveredQuantity?: number;
  remainingDeliveryQuantity?: number;
  invoicedAmountPaisa?: number;
  remainingInvoiceAmountPaisa?: number;
  chequeReleasedAmountPaisa?: number;
  chequeClearedAmountPaisa?: number;
  remainingPaymentAmountPaisa?: number;
  hasSubParts: boolean | null;
  valuePaisa?: number | null;
  gstAmountPaisa?: number | null;
  totalValuePaisa?: number | null;
}

export const projectItemsKey = (projectId: string) => ['projects', projectId, 'items'] as const;

export function useProjectItems(projectId: string) {
  return useQuery({
    queryKey: projectItemsKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest<ProjectItem[]>(`/api/projects/${projectId}/items`, { signal }),
  });
}

export interface CreatePartInput {
  itemType: 'PART';
  srNumber: number;
  itemNumber: string;
  name: string;
  uom: string;
  partQuantity: number;
  clientUnitPricePaisa: number;
  gstRateBps: number;
  supplierUnitPricePaisa?: number;
  makeBrand?: string | null;
  origin?: string | null;
  remarks?: string | null;
}
export interface CreateSubPartInput {
  itemType: 'SUB_PART';
  parentItemId: string;
  srNumber: number;
  itemNumber: string;
  name: string;
  quantityPerParent: number;
  supplierUnitPricePaisa?: number;
  makeBrand?: string | null;
  origin?: string | null;
  remarks?: string | null;
}

export function useCreateProjectItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectItem, ApiError, CreatePartInput | CreateSubPartInput>({
    mutationFn: (body) =>
      apiRequest<ProjectItem>(`/api/projects/${projectId}/items`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}



export interface ScheduleCsvImportIssue {
  rowNumber: number;
  message: string;
}

export interface ScheduleCsvImportParsedRow {
  rowNumber: number;
  itemType: ItemType;
  srNumber: number;
  itemNumber: string;
  name: string;
  uom: string | null;
  quantity: number;
  parent: string | null;
  clientUnitPricePaisa: number | null;
  gstRateBps: number | null;
  supplierUnitPricePaisa: number | null;
  makeBrand: string | null;
  origin: string | null;
  remarks: string | null;
}

export interface ScheduleCsvImportResult {
  dryRun: boolean;
  rowsRead: number;
  rowsValid: number;
  rowsImported: number;
  errors: ScheduleCsvImportIssue[];
  warnings: ScheduleCsvImportIssue[];
  parsedRows: ScheduleCsvImportParsedRow[];
  items: ProjectItem[];
}

export function useImportScheduleCsv(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ScheduleCsvImportResult, ApiError, { csvText: string; dryRun?: boolean }>({
    mutationFn: (body) =>
      apiRequest<ScheduleCsvImportResult>(`/api/projects/${projectId}/items/import-csv`, {
        method: 'POST',
        body,
      }),
    onSuccess: (result) => {
      if (!result.dryRun && result.rowsImported > 0) {
        void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
        void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
        void qc.invalidateQueries({ queryKey: ['visibility'] });
      }
    },
  });
}

export function useDeleteProjectItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    null,
    ApiError,
    {
      itemId: string;
      rowVersion: number;
      acknowledgeDeleteWithBilling?: boolean;
      acknowledgeDeleteWithActivePipeline?: boolean;
      acknowledgeDeleteSubPartWithBatches?: boolean;
    }
  >({
    mutationFn: ({
      itemId,
      rowVersion,
      acknowledgeDeleteWithBilling,
      acknowledgeDeleteWithActivePipeline,
      acknowledgeDeleteSubPartWithBatches,
    }) =>
      apiRequest<null>(`/api/projects/${projectId}/items/${itemId}`, {
        method: 'DELETE',
        query: {
          rowVersion,
          ...(acknowledgeDeleteWithBilling ? { acknowledgeDeleteWithBilling: true } : {}),
          ...(acknowledgeDeleteWithActivePipeline
            ? { acknowledgeDeleteWithActivePipeline: true }
            : {}),
          ...(acknowledgeDeleteSubPartWithBatches
            ? { acknowledgeDeleteSubPartWithBatches: true }
            : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

/**
 * Edit a part (PART or SUB_PART). Pass 23 surfaces `acknowledgeReducedQuantity`
 * for the admin warning gate (qty reduction below active-ordered).
 */
export interface UpdateProjectItemInput {
  itemId: string;
  rowVersion: number;
  srNumber?: number;
  itemNumber?: string | null;
  name?: string;
  uom?: string | null;
  partQuantity?: number;
  quantityPerParent?: number;
  clientUnitPricePaisa?: number | null;
  gstRateBps?: number | null;
  supplierUnitPricePaisa?: number | null;
  makeBrand?: string | null;
  origin?: string | null;
  remarks?: string | null;
  acknowledgeReducedQuantity?: boolean;
}

export function useUpdateProjectItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectItem, ApiError, UpdateProjectItemInput>({
    mutationFn: ({ itemId, ...body }) =>
      apiRequest<ProjectItem>(`/api/projects/${projectId}/items/${itemId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Parts approval workflow
// ---------------------------------------------------------------------------

export type SubmissionStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'APPROVED_WITH_EDITS'
  | 'REJECTED'
  | 'SUPERSEDED';
export type SubmissionReviewAction = 'APPROVE' | 'APPROVE_WITH_EDITS' | 'REJECT' | 'FINALIZE';

export interface PartsSubmission {
  id: string;
  projectId: string;
  submittedByUserId: string;
  submittedAt: string;
  status: SubmissionStatus;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  reviewAction: SubmissionReviewAction | null;
  rejectionComment: string | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export const partsSubmissionsKey = (projectId: string) =>
  ['projects', projectId, 'submissions'] as const;

export function usePartsSubmissions(projectId: string) {
  return useQuery({
    queryKey: partsSubmissionsKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest<PartsSubmission[]>(`/api/projects/${projectId}/parts-submissions`, { signal }),
  });
}

export function useSubmitParts(projectId: string) {
  const qc = useQueryClient();
  return useMutation<PartsSubmission, ApiError, void>({
    mutationFn: () =>
      apiRequest<PartsSubmission>(`/api/projects/${projectId}/parts-submissions`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partsSubmissionsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export function useApproveSubmission(projectId: string) {
  const qc = useQueryClient();
  return useMutation<PartsSubmission, ApiError, { submissionId: string }>({
    mutationFn: ({ submissionId }) =>
      apiRequest<PartsSubmission>(
        `/api/projects/${projectId}/parts-submissions/${submissionId}/approve`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partsSubmissionsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export function useRejectSubmission(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    PartsSubmission,
    ApiError,
    { submissionId: string; rejectionComment?: string }
  >({
    mutationFn: ({ submissionId, rejectionComment }) =>
      apiRequest<PartsSubmission>(
        `/api/projects/${projectId}/parts-submissions/${submissionId}/reject`,
        { method: 'POST', body: { rejectionComment } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partsSubmissionsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export function useFinalizeParts(projectId: string) {
  const qc = useQueryClient();
  return useMutation<PartsSubmission, ApiError, void>({
    mutationFn: () =>
      apiRequest<PartsSubmission>(`/api/projects/${projectId}/parts-submissions/finalize`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partsSubmissionsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Order batches
// ---------------------------------------------------------------------------

export type OrderBatchStatus = 'ACTIVE' | 'CANCELLED' | 'REPLACED';

export interface OrderBatch {
  id: string;
  projectItemId: string;
  supplierId: string;
  supplierNameSnapshot: string;
  quantity: number;
  orderPlacedDate: string;
  expectedArrivalDate: string;
  preShipmentInspectionDate: string | null;
  inTransitDate: string | null;
  arrivedDate: string | null;
  inHouseInspectionDate: string | null;
  status: OrderBatchStatus;
  cancellationDate: string | null;
  cancellationReason: string | null;
  cancelledByUserId: string | null;
  replacedByBatchId: string | null;
  replacesBatchId: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
  // Derived
  currentStageNumber: 1 | 2 | 3 | 4 | 5;
  daysInCurrentStage: number;
  isOverdue: boolean;
  supplierName: string;
}

export const batchesKey = (projectId: string, itemId: string) =>
  ['projects', projectId, 'items', itemId, 'batches'] as const;

export function useBatches(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId
      ? batchesKey(projectId, itemId)
      : ['projects', projectId, 'items', 'none', 'batches'],
    queryFn: ({ signal }) =>
      apiRequest<OrderBatch[]>(`/api/projects/${projectId}/items/${itemId}/batches`, { signal }),
    enabled: Boolean(itemId),
  });
}

export function useCreateBatch(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    OrderBatch,
    ApiError,
    {
      supplierId: string;
      quantity: number;
      orderPlacedDate: string;
      expectedArrivalDate: string;
    }
  >({
    mutationFn: (body) =>
      apiRequest<OrderBatch>(`/api/projects/${projectId}/items/${itemId}/batches`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}


export interface BulkCreateBatchInput {
  projectItemId: string;
  supplierId: string;
  quantity: number;
  orderPlacedDate: string;
  expectedArrivalDate: string;
}

export function useCreateBulkBatches(projectId: string) {
  const qc = useQueryClient();
  return useMutation<OrderBatch[], ApiError, { batches: BulkCreateBatchInput[] }>({
    mutationFn: (body) =>
      apiRequest<OrderBatch[]>(`/api/projects/${projectId}/batches/bulk`, {
        method: 'POST',
        body,
      }),
    onSuccess: (_rows, variables) => {
      const itemIds = new Set(variables.batches.map((b) => b.projectItemId));
      for (const itemId of itemIds) {
        void qc.invalidateQueries({ queryKey: batchesKey(projectId, itemId) });
      }
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export interface UpdateBatchInput {
  supplierId?: string;
  quantity?: number;
  orderPlacedDate?: string;
  expectedArrivalDate?: string;
  preShipmentInspectionDate?: string | null;
  inTransitDate?: string | null;
  arrivedDate?: string | null;
  inHouseInspectionDate?: string | null;
  rowVersion: number;
}

export function useUpdateBatch(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<OrderBatch, ApiError, { batchId: string } & UpdateBatchInput>({
    mutationFn: ({ batchId, ...body }) =>
      apiRequest<OrderBatch>(`/api/projects/${projectId}/items/${itemId}/batches/${batchId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

export function useCancelBatch(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    OrderBatch,
    ApiError,
    { batchId: string; cancellationReason: string; rowVersion: number }
  >({
    mutationFn: ({ batchId, ...body }) =>
      apiRequest<OrderBatch>(
        `/api/projects/${projectId}/items/${itemId}/batches/${batchId}/cancel`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

/**
 * Replace an ACTIVE batch with a new one. The old batch transitions to
 * REPLACED; the new batch is inserted with `replacesBatchId` pointing back.
 * Admin-only on the server.
 */
export interface ReplaceBatchInput {
  batchId: string;
  rowVersion: number;
  cancellationReason: string;
  replacement: {
    supplierId: string;
    quantity: number;
    orderPlacedDate: string;
    expectedArrivalDate: string;
  };
}

export function useReplaceBatch(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<OrderBatch, ApiError, ReplaceBatchInput>({
    mutationFn: ({ batchId, ...body }) =>
      apiRequest<OrderBatch>(
        `/api/projects/${projectId}/items/${itemId}/batches/${batchId}/replace`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}


// ---------------------------------------------------------------------------
// Delivery events (partial/final deliveries feeding stage 12)
// ---------------------------------------------------------------------------

export interface DeliveryEvent {
  id: string;
  projectItemId: string;
  quantity: number;
  deliveryDate: string;
  referenceNumber: string | null;
  remarks: string | null;
  recordedByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export const deliveryEventsKey = (projectId: string, itemId: string) =>
  ['projects', projectId, 'items', itemId, 'deliveries'] as const;

export function useDeliveryEvents(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId
      ? deliveryEventsKey(projectId, itemId)
      : ['projects', projectId, 'items', 'none', 'deliveries'],
    queryFn: ({ signal }) =>
      apiRequest<DeliveryEvent[]>(`/api/projects/${projectId}/items/${itemId}/deliveries`, {
        signal,
      }),
    enabled: Boolean(itemId),
  });
}

export function useCreateDeliveryEvent(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    DeliveryEvent,
    ApiError,
    { quantity: number; deliveryDate: string; referenceNumber?: string | null; remarks?: string | null }
  >({
    mutationFn: (body) =>
      apiRequest<DeliveryEvent>(`/api/projects/${projectId}/items/${itemId}/deliveries`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliveryEventsKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: partStageDatesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}


// ---------------------------------------------------------------------------
// Payment events (partial/final billing amounts feeding stages 16-18)
// ---------------------------------------------------------------------------

export type PaymentEventType = 'INVOICE_SUBMITTED' | 'CHEQUE_RELEASED' | 'CHEQUE_CLEARED';

export interface PaymentEvent {
  id: string;
  projectItemId: string;
  eventType: PaymentEventType;
  amountPaisa: number;
  eventDate: string;
  referenceNumber: string | null;
  remarks: string | null;
  recordedByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export const paymentEventsKey = (projectId: string, itemId: string) =>
  ['projects', projectId, 'items', itemId, 'payments'] as const;

export function usePaymentEvents(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId
      ? paymentEventsKey(projectId, itemId)
      : ['projects', projectId, 'items', 'none', 'payments'],
    queryFn: ({ signal }) =>
      apiRequest<PaymentEvent[]>(`/api/projects/${projectId}/items/${itemId}/payments`, {
        signal,
      }),
    enabled: Boolean(itemId),
  });
}

export function useCreatePaymentEvent(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    PaymentEvent,
    ApiError,
    {
      eventType: PaymentEventType;
      amountPaisa: number;
      eventDate: string;
      referenceNumber?: string | null;
      remarks?: string | null;
    }
  >({
    mutationFn: (body) =>
      apiRequest<PaymentEvent>(`/api/projects/${projectId}/items/${itemId}/payments`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: paymentEventsKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: partStageDatesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Part-stage dates (stages 6, 7, 8, 10, 12, 14-18)
// ---------------------------------------------------------------------------

export type PartStageNumber = 6 | 7 | 8 | 10 | 12 | 14 | 15 | 16 | 17 | 18;

export interface PartStageDate {
  id: string;
  projectItemId: string;
  stageNumber: PartStageNumber;
  completedDate: string;
  recordedByUserId: string;
  recordedAt: string;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export const partStageDatesKey = (projectId: string, itemId: string) =>
  ['projects', projectId, 'items', itemId, 'stage-dates'] as const;

export function usePartStageDates(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId
      ? partStageDatesKey(projectId, itemId)
      : ['projects', projectId, 'items', 'none', 'stage-dates'],
    queryFn: ({ signal }) =>
      apiRequest<PartStageDate[]>(`/api/projects/${projectId}/items/${itemId}/stage-dates`, {
        signal,
      }),
    enabled: Boolean(itemId),
  });
}

export function useUpsertPartStageDate(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    PartStageDate,
    ApiError,
    { stageNumber: PartStageNumber; completedDate: string; rowVersion?: number }
  >({
    mutationFn: ({ stageNumber, ...body }) =>
      apiRequest<PartStageDate>(
        `/api/projects/${projectId}/items/${itemId}/stage-dates/${stageNumber}`,
        { method: 'PUT', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: partStageDatesKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Inspection events (stages 9, 11, 13)
// ---------------------------------------------------------------------------

export type InspectionStageNumber = 9 | 11 | 13;
export type InspectionOutcome = 'ACCEPTED' | 'OBSERVATION' | 'REJECTED';
export type InspectionLocation = 'OUR_PREMISES' | 'CLIENT_PREMISES' | 'THIRD_PARTY_PREMISES' | 'TBD';

export interface InspectionEvent {
  id: string;
  projectItemId: string;
  stageNumber: InspectionStageNumber;
  eventRound: number;
  eventDate: string;
  outcome: InspectionOutcome;
  notes: string | null;
  resolvedDate: string | null;
  location: InspectionLocation | null;
  recordedByUserId: string;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export const inspectionEventsKey = (projectId: string, itemId: string) =>
  ['projects', projectId, 'items', itemId, 'inspections'] as const;

export function useInspectionEvents(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId
      ? inspectionEventsKey(projectId, itemId)
      : ['projects', projectId, 'items', 'none', 'inspections'],
    queryFn: ({ signal }) =>
      apiRequest<InspectionEvent[]>(
        `/api/projects/${projectId}/items/${itemId}/inspection-events`,
        { signal },
      ),
    enabled: Boolean(itemId),
  });
}

export function useCreateInspectionEvent(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    InspectionEvent,
    ApiError,
    {
      stageNumber: InspectionStageNumber;
      eventDate: string;
      outcome: InspectionOutcome;
      notes?: string;
      resolvedDate?: string;
      location?: InspectionLocation;
    }
  >({
    mutationFn: (body) =>
      apiRequest<InspectionEvent>(`/api/projects/${projectId}/items/${itemId}/inspection-events`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: inspectionEventsKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

/**
 * Update an inspection event — most commonly to record the resolvedDate on a
 * non-ACCEPTED outcome, but the API also allows changing outcome/notes/date
 * with the appropriate rowVersion guard.
 */
export function useUpdateInspectionEvent(projectId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation<
    InspectionEvent,
    ApiError,
    {
      eventId: string;
      rowVersion: number;
      eventDate?: string;
      outcome?: InspectionOutcome;
      notes?: string | null;
      resolvedDate?: string | null;
      location?: InspectionLocation | null;
    }
  >({
    mutationFn: ({ eventId, ...body }) =>
      apiRequest<InspectionEvent>(
        `/api/projects/${projectId}/items/${itemId}/inspection-events/${eventId}`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: inspectionEventsKey(projectId, itemId) });
      void qc.invalidateQueries({ queryKey: projectItemsKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Bank Guarantees
// ---------------------------------------------------------------------------

export type BgStatus = 'ACTIVE' | 'ACCEPTED' | 'ENCASHED' | 'RELEASED' | 'REPLACED';

export interface BankGuarantee {
  id: string;
  projectId: string;
  amountPaisa: number;
  submittedDate: string | null;
  acceptedDate: string | null;
  appliedDate: string | null;
  releasedDate: string | null;
  replacesBgId: string | null;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
  status: BgStatus;
}

export const bankGuaranteesKey = (projectId: string) =>
  ['projects', projectId, 'bank-guarantees'] as const;

export function useBankGuarantees(projectId: string) {
  return useQuery({
    queryKey: bankGuaranteesKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest<BankGuarantee[]>(`/api/projects/${projectId}/bank-guarantees`, {
        signal,
      }),
  });
}

export function useCreateBg(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    BankGuarantee,
    ApiError,
    {
      amountPaisa: number;
      submittedDate?: string;
      replacesBgId?: string;
      notes?: string;
    }
  >({
    mutationFn: (body) =>
      apiRequest<BankGuarantee>(`/api/projects/${projectId}/bank-guarantees`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: bankGuaranteesKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

export function useUpdateBg(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    BankGuarantee,
    ApiError,
    {
      bgId: string;
      amountPaisa?: number;
      submittedDate?: string | null;
      acceptedDate?: string | null;
      appliedDate?: string | null;
      releasedDate?: string | null;
      notes?: string | null;
      rowVersion: number;
    }
  >({
    mutationFn: ({ bgId, ...body }) =>
      apiRequest<BankGuarantee>(`/api/projects/${projectId}/bank-guarantees/${bgId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: bankGuaranteesKey(projectId) });
      void qc.invalidateQueries({ queryKey: projectQueryKey(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Suppliers list — for batch create dropdown
// ---------------------------------------------------------------------------

export interface Supplier {
  id: string;
  name: string;
  isActive: boolean;
}

interface PaginatedList<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const suppliersListKey = ['suppliers'] as const;

export function useSuppliersList() {
  return useQuery({
    queryKey: suppliersListKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<Supplier>>('/api/suppliers?pageSize=200', {
        signal,
      });
      return res.items.filter((s) => s.isActive);
    },
    staleTime: 5 * 60_000,
  });
}
